import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ListItem,
  ListItemText,
  List,
  ListItemSecondaryAction,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { templateService } from "../../utils/services/template-service";
import { formatKey } from "../../utils/utility";

const OPERATOR_OPTIONS = {
  equals: "Equals",
  startsWith: "Starts With",
  endsWith: "Ends With",
  contains: "Contains",
  equalsIgnoreCase: "Equals (Case Insensitive)",
  combine: "Combine",
};

const TemplateManagement = ({ displayKey, id, onReturn, type, schema }) => {
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add', 'edit', 'view'
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: "", mapped_column: [] });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await templateService.fetch(type);
      setTemplates(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddClick = () => {
    setModalMode("add");
    setFormData({
      name: "",
      mapped_column: schema.map((key, index) => ({
        key,
        column: "",
        order: index + 1,
        operator: "",
      })),
    });
    setCurrentTemplate(null);
    setModalOpen(true);
  };

  const handleEditClick = (template) => {
    setModalMode("edit");
    const mappedColumn = template.mapped_column || [];
    // Ensure each mapping has an order field, add if missing
    const updatedMappedColumn = mappedColumn.map((mapping, index) => ({
      ...mapping,
      order: mapping.order || index + 1,
    }));
    setFormData({
      name: template.name || "",
      mapped_column: updatedMappedColumn,
    });
    setCurrentTemplate(template);
    setModalOpen(true);
  };

  const handleViewClick = (template) => {
    setModalMode("view");
    setFormData({
      name: template.name || "",
      mapped_column: template.mapped_column || [],
    });
    setCurrentTemplate(template);
    setModalOpen(true);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (modalMode === "add") {
        let resp = await templateService.create({
          name: formData.name,
          mapped_column: formData.mapped_column,
          type: type,
        });
        setTemplates([...templates, resp]);
      } else if (modalMode === "edit") {
        let resp = await templateService.patch(currentTemplate[id], {
          name: formData.name,
          mapped_column: formData.mapped_column,
          type: type,
        });
        const updatedTemplates = templates.map((template) =>
          template[id] === currentTemplate[id] ? resp : template,
        );
        setTemplates(updatedTemplates);
      }
      setModalOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleMappingChange = (index, field, value) => {
    const newMappings = formData.mapped_column.map((mapping, i) =>
      i === index ? { ...mapping, [field]: value } : mapping,
    );
    setFormData({ ...formData, mapped_column: newMappings });
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      try {
        let resp = await templateService.delete(templateToDelete[id]);
        if (resp) {
          fetchTemplates();
        }
      } catch (error) {
        console.error("Error deleting template:", error);
      }
    }
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: "60vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "poppins",
              fontWeight: 500,
              fontSize: { sm: "18px", md: "24px", xs: "15px" },
              color: "var(--text-primary)",
            }}
          >
            {`${String(type).toPascalCase()} Templates`}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: "secondary.main",
            }}
            onClick={handleAddClick}
          >
            <Typography
              sx={{
                fontFamily: "poppins",
                fontWeight: 500,
                fontSize: { sm: "12px", md: "14px", xs: "12px" },
                color: "var(--text-primary)",
              }}
            >
              Add
            </Typography>
          </Button>
        </Box>
        <List sx={{ flex: 1, overflow: "auto" }}>
          {templates.map((template, index) => (
            <ListItem key={index} divider>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontFamily: "poppins",
                      fontWeight: 400,
                      fontSize: { sm: "14px", md: "16px", xs: "12px" },
                      color: "var(--text-primary)",
                    }}
                  >
                    {template[displayKey]}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleViewClick(template)}
                >
                  <ViewIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => handleEditClick(template)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  disabled={template?.name?.toLowerCase() === "default"}
                  edge="end"
                  onClick={() => handleDeleteClick(template)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
            }}
            onClick={onReturn}
          >
            <Typography
              sx={{
                fontFamily: "poppins",
                fontWeight: 500,
                fontSize: { sm: "12px", md: "14px", xs: "12px" },
                color: "var(--text-primary)",
              }}
            >
              Return to Template Selection
            </Typography>
          </Button>
        </Box>
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {modalMode === "add"
            ? "Add Template"
            : modalMode === "edit"
              ? "Edit Template"
              : "View Template"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={modalMode === "view"}
          />
          {modalMode === "view" ? (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Column</TableCell>
                    <TableCell>Operator</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.mapped_column
                    .sort((a, b) => a.order - b.order)
                    .map((col, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{col.order}</TableCell>
                        <TableCell>{col.key}</TableCell>
                        <TableCell>{col.column}</TableCell>
                        <TableCell>
                          {OPERATOR_OPTIONS[col.operator] || "--"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Column</TableCell>
                    <TableCell>Operator</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.mapped_column.map((mapping, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={mapping.order}
                          onChange={(e) =>
                            handleMappingChange(
                              index,
                              "order",
                              parseInt(e.target.value) || 1,
                            )
                          }
                          inputProps={{ min: 1 }}
                        />
                      </TableCell>
                      <TableCell>{formatKey(mapping.key)}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={mapping.column}
                          onChange={(e) =>
                            handleMappingChange(index, "column", e.target.value)
                          }
                          placeholder={
                            mapping.operator === "combine"
                              ? "Col A, Col B, Col C"
                              : undefined
                          }
                          helperText={
                            mapping.operator === "combine"
                              ? "Comma-separated column names to combine"
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          fullWidth
                          size="small"
                          displayEmpty
                          value={mapping.operator || ""}
                          onChange={(e) =>
                            handleMappingChange(
                              index,
                              "operator",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="">--</MenuItem>
                          {Object.entries(OPERATOR_OPTIONS).map(
                            ([value, label]) => (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          {modalMode !== "view" && (
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "
            {templateToDelete?.[displayKey]}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateManagement;
